"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Award, BookOpen, Clock, Play } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { CourseCompletion } from "@/lib/api/courses";

/* ------------------------------------------------------------------ */
/*  Image with logo fallback                                           */
/* ------------------------------------------------------------------ */

function CourseThumbnail({
  src,
  alt,
  fill = true,
  sizes,
  className = "",
}: {
  src: string | null;
  alt: string;
  fill?: boolean;
  sizes?: string;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <div className="flex h-full items-center justify-center">
        <svg
          viewBox="0 0 120 40"
          className="h-8 w-24 opacity-20"
          aria-label="Grow logo"
        >
          <text
            x="50%"
            y="50%"
            dominantBaseline="central"
            textAnchor="middle"
            className="fill-current"
            style={{ fontSize: "28px", fontWeight: 700, fontFamily: "system-ui, sans-serif" }}
          >
            grow.
          </text>
        </svg>
      </div>
    );
  }

  return (
    <Image
      src={src}
      alt={alt}
      fill={fill}
      sizes={sizes}
      className={className}
      onError={() => setFailed(true)}
    />
  );
}

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface FeaturedCourseSummary {
  slug: string;
  title: string;
  subtitle: string;
  tagline: string;
  category: string;
  level: string;
  estimatedMinutes: number;
  moduleCount: number;
  lessonCount: number;
  assessmentCount: number;
  featuredOutcomes: string[];
  thumbnail?: string | null;
  heroImageUrl?: string | null;
}

export interface CatalogCourse {
  slug: string;
  title: string;
  category: string;
  lessonCount: number;
  estimatedMinutes: number;
  thumbnail: string | null;
  heroImageUrl: string | null;
}

interface Home1ClientProps {
  featuredCourse: FeaturedCourseSummary | null;
  catalogCourses?: CatalogCourse[];
  recentCompletions?: CourseCompletion[];
  completionCount?: number;
  isAuthenticated?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Completions ticker                                                 */
/* ------------------------------------------------------------------ */

function CompletionsTicker({
  completions,
  totalCount,
}: {
  completions: CourseCompletion[];
  totalCount: number;
}) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (completions.length <= 1) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % completions.length), 4000);
    return () => clearInterval(id);
  }, [completions.length]);

  const c = completions[index];
  if (!c) return null;

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
      {/* Stat */}
      {totalCount > 0 && (
        <p className="text-sm font-medium text-white/40">
          <span className="tabular-nums text-white/70">{totalCount.toLocaleString()}</span>{" "}
          courses completed
        </p>
      )}

      {/* Divider — desktop only */}
      {totalCount > 0 && (
        <span className="hidden h-4 w-px bg-white/15 sm:block" />
      )}

      {/* Rotating name */}
      <div className="flex h-6 items-center overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.p
            key={index}
            initial={{ y: 14, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -14, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="text-sm text-white/40"
          >
            <span className="text-white/60">{c.userName}</span> completed{" "}
            <span className="text-white/60">{c.courseTitle}</span>
          </motion.p>
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export function Home1Client({
  featuredCourse,
  catalogCourses = [],
  recentCompletions = [],
  completionCount = 0,
  isAuthenticated = false,
}: Home1ClientProps) {
  const outcomes = featuredCourse?.featuredOutcomes ?? [];
  const featuredImage =
    featuredCourse?.thumbnail ?? featuredCourse?.heroImageUrl ?? null;

  return (
    <div className="bg-background">
      {/* ============================================================ */}
      {/*  HERO                                                        */}
      {/* ============================================================ */}
      <section className="relative isolate overflow-hidden bg-neutral-950">
        <div className="absolute inset-0">
          <Image
            src="/images/53769603456_d0ccc9fccd_k.jpg"
            alt=""
            fill
            priority
            className="object-cover"
          />
          <div className="absolute inset-0 bg-neutral-950/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/40 to-transparent" />
        </div>

        <div className="container relative z-10 mx-auto px-4 pb-20 pt-28 sm:pb-28 sm:pt-36 md:pb-36 md:pt-44">
          {/* Social proof ticker + stat */}
          {recentCompletions.length > 0 && (
            <div className="mb-8">
              <CompletionsTicker
                completions={recentCompletions}
                totalCount={completionCount}
              />
            </div>
          )}

          <h1 className="max-w-3xl text-4xl font-extrabold leading-[1.1] tracking-tight text-white sm:text-5xl md:text-6xl lg:text-7xl">
            Your next step starts&nbsp;here.
          </h1>

          <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/60">
            Video courses, interactive assessments, and certificates — built for
            your church community.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            {/* Personalized CTA */}
            <Button
              asChild
              size="lg"
              className="h-12 rounded-full bg-white px-8 text-sm font-semibold text-neutral-950 hover:bg-white/90"
            >
              <Link href={isAuthenticated ? "/dashboard" : "/courses"}>
                {isAuthenticated ? "Continue learning" : "Browse courses"}
              </Link>
            </Button>

            {featuredCourse && (
              <Link
                href={`/courses/${featuredCourse.slug}`}
                className="group flex items-center gap-2 text-sm font-medium text-white/70 transition-colors hover:text-white"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 transition-colors group-hover:border-white/40">
                  <Play className="h-3.5 w-3.5 fill-current" />
                </span>
                Start featured course
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  FEATURED COURSE                                             */}
      {/* ============================================================ */}
      {featuredCourse && (
        <section className="bg-neutral-950 pb-20 pt-4 sm:pb-28">
          <div className="container">
            <div className="grid items-stretch gap-0 overflow-hidden rounded-2xl bg-neutral-900 lg:grid-cols-2">
              {/* Image half */}
              <div className="relative min-h-[300px] bg-neutral-800 text-neutral-500 sm:min-h-[400px]">
                <CourseThumbnail
                  src={featuredImage}
                  alt={featuredCourse.title}
                  className="object-cover"
                  sizes="(min-width: 1024px) 50vw, 100vw"
                />
              </div>

              {/* Content half */}
              <div className="flex flex-col justify-center p-8 sm:p-12">
                <p className="text-xs font-semibold uppercase tracking-widest text-neutral-400">
                  Featured &middot; {featuredCourse.category}
                </p>

                <h2 className="mt-4 text-2xl font-bold text-white sm:text-3xl">
                  {featuredCourse.title}
                </h2>

                <p className="mt-3 text-neutral-400">
                  {featuredCourse.subtitle || featuredCourse.tagline}
                </p>

                <div className="mt-6 flex flex-wrap gap-5 text-sm text-neutral-400">
                  <span className="flex items-center gap-1.5">
                    <Clock className="h-4 w-4 text-neutral-500" />
                    {featuredCourse.estimatedMinutes} min
                  </span>
                  <span className="flex items-center gap-1.5">
                    <BookOpen className="h-4 w-4 text-neutral-500" />
                    {featuredCourse.lessonCount} lessons
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Award className="h-4 w-4 text-neutral-500" />
                    {featuredCourse.assessmentCount} assessments
                  </span>
                </div>

                {outcomes.length > 0 && (
                  <ul className="mt-6 space-y-2 border-t border-neutral-800 pt-6 text-sm text-neutral-300">
                    {outcomes.slice(0, 3).map((o) => (
                      <li key={o} className="flex items-start gap-2">
                        <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-white" />
                        {o}
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mt-8">
                  <Button
                    asChild
                    size="lg"
                    className="h-12 rounded-full bg-white px-8 text-sm font-semibold text-neutral-950 hover:bg-white/90"
                  >
                    <Link href={`/courses/${featuredCourse.slug}`}>
                      Start this course
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============================================================ */}
      {/*  DARK → LIGHT TRANSITION                                     */}
      {/* ============================================================ */}
      {catalogCourses.length > 0 && (
        <div className="h-24 bg-gradient-to-b from-neutral-950 to-background sm:h-32" />
      )}

      {/* ============================================================ */}
      {/*  COURSE CATALOG                                              */}
      {/* ============================================================ */}
      {catalogCourses.length > 0 && (
        <section className="pb-16 sm:pb-24">
          <div className="container">
            <div className="flex items-end justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-foreground sm:text-3xl">
                  Explore courses
                </h2>
                <p className="mt-2 text-muted-foreground">
                  Self-paced video courses with assessments and certificates.
                </p>
              </div>
              <Link
                href="/courses"
                className="hidden shrink-0 text-sm font-medium text-foreground underline-offset-4 hover:underline sm:inline-block"
              >
                View all
              </Link>
            </div>

            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {catalogCourses.map((course) => {
                const img = course.thumbnail ?? course.heroImageUrl;
                return (
                  <Link
                    key={course.slug}
                    href={`/courses/${course.slug}`}
                    className="group overflow-hidden rounded-xl border border-border bg-background transition-shadow hover:shadow-lg"
                  >
                    {/* Thumbnail with play overlay */}
                    <div className="relative aspect-[16/9] bg-muted text-muted-foreground">
                      <CourseThumbnail
                        src={img}
                        alt={course.title}
                        className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                        sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                      />
                      {/* Play icon on hover */}
                      <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 transition-colors duration-300 group-hover:bg-black/30">
                        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90 text-neutral-950 opacity-0 shadow-lg transition-all duration-300 group-hover:opacity-100 group-hover:scale-100 scale-75">
                          <Play className="h-5 w-5 fill-current ml-0.5" />
                        </span>
                      </div>
                    </div>

                    {/* Meta */}
                    <div className="p-4">
                      <p className="text-xs font-medium text-muted-foreground">
                        {course.category}
                      </p>
                      <h3 className="mt-1 font-semibold leading-snug text-foreground group-hover:underline">
                        {course.title}
                      </h3>
                      <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <BookOpen className="h-3 w-3" />
                          {course.lessonCount} lessons
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {course.estimatedMinutes} min
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            <div className="mt-8 text-center sm:hidden">
              <Link
                href="/courses"
                className="text-sm font-medium text-foreground underline-offset-4 hover:underline"
              >
                View all courses
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
